import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import { UserDropdown }  from "CommonDropdowns";
import validate from "validate.js";
import { createOrUpdateProgram } from "pages/AdminManage/actions";

const schema = {
  program_name: {
    presence: { allowEmpty: false, message: "^Program name is required" },
  },
  manager_id: {
    presence: { allowEmpty: false, message: "^Manager is required" },
  },
  tracking: {
    presence: { allowEmpty: false, message: "^Tracking option is required" },
  },
};

const AddUpdateModal = ({
  show,
  handleClose,
  isEdit = false,
  object_viewed_id,
}) => {
  const dispatch = useDispatch();
  const programState = useSelector((s) => s.programState);
  const { programRecord, validation_error, redirectToList } = programState;

  const [formState, setFormState] = useState({
    isValid: false,
    values: { is_active: "y" },
    touched: {},
    errors: {},
  });

  const [userValue, setUserValue] = useState(null);

  useEffect(() => {
    if (isEdit && programRecord) {
      const values = {
        id: programRecord.id,
        program_name: programRecord.program_name || "",
        manager_id: programRecord.manager_id || "",
        tracking: programRecord.tracking?.toString() ?? "", // normalize to string
        is_active: programRecord.is_active || "y",
        address: programRecord.address || "",
        postal_code: programRecord.postal_code || "",
        payment_term: programRecord.payment_term || "",
        state: programRecord.state || "",
        employer_id: programRecord.employer_id || "",
        emails: programRecord.emails || "",
      };

      setFormState((fs) => ({
        ...fs,
        values,
      }));

      if (programRecord.manager) {
        setUserValue({
          label: programRecord.manager.name,
          value: programRecord.manager.id,
        });
      }
    }
  }, [programRecord, isEdit]);

  useEffect(() => {
    if (validation_error) {
      setFormState((fs) => ({
        ...fs,
        isValid: false,
        errors: validation_error || {},
      }));
    }
  }, [validation_error]);

  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState((fs) => ({
      ...fs,
      isValid: !errors,
      errors: errors || {},
    }));
  }, [formState.values]);

  useEffect(() => {
    if (redirectToList) {
      handleClose();
    }
  }, [redirectToList]);

  const trackingOptions = [
    { value: "1", label: "Yes" },
    { value: "0", label: "No" },
  ];

  const handleChange = (event) => {
    event.persist();
    setFormState((fs) => ({
      ...fs,
      values: { ...fs.values, [event.target.name]: event.target.value },
      touched: { ...fs.touched, [event.target.name]: true },
    }));
  };

  const handleManagerChange = (selected) => {
    setUserValue(selected);
    setFormState((fs) => ({
      ...fs,
      values: { ...fs.values, manager_id: selected ? selected.value : "" },
      touched: { ...fs.touched, manager_id: true },
    }));
  };

  const handleTrackingChange = (selected) => {
    setFormState((fs) => ({
      ...fs,
      values: { ...fs.values, tracking: selected ? selected.value : "" },
      touched: { ...fs.touched, tracking: true },
    }));
  };

  const handleActiveToggle = (event) => {
    const checked = event.target.checked;
    setFormState((fs) => ({
      ...fs,
      values: { ...fs.values, is_active: checked ? "y" : "n" },
      touched: { ...fs.touched, is_active: true },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const method = formState.values.id ? "PUT" : "POST";
    dispatch(createOrUpdateProgram(formState.values, method, object_viewed_id));
  };

  const hasError = (field) =>
    formState.touched[field] && formState.errors[field] ? true : false;

  const onModelClose = () => {
    setFormState({
      isValid: false,
      values: { is_active: "y" },
      touched: {},
      errors: {},
    });
    setUserValue(null);
    dispatch({ type: "RESET_PROGRAM_RECORD" }); // optional clean-up in reducer
    handleClose();
  };

  return (
    <Modal
      show={show}
      onHide={onModelClose}
      centered
      className="add-new-program-admin"
    >
      <div className="p-2">
        <Modal.Header className="p-2" closeButton>
          <Modal.Title>
            <h5 className="font-20 font-weight-bold heading-color">
              {isEdit ? "Update Program" : "Add New Program"}
            </h5>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col lg={12}>
                <Form.Group className="mb-2">
                  <Form.Label className="label-input">Program</Form.Label>
                  <Form.Control
                    type="text"
                    name="program_name"
                    placeholder="Type a program name"
                    className={`input-field ${
                      hasError("program_name") ? "is-invalid" : ""
                    }`}
                    onChange={handleChange}
                    value={formState.values.program_name || ""}
                  />
                  {hasError("program_name") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.program_name[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col lg={6}>
                <UserDropdown
                  isMulti={false}
                  selectedId={formState.values.manager_id || ""}
                  userValue={userValue}
                  setUserValue={setUserValue}
                  userOnChange={handleManagerChange}
                  placeholder="Select Manager"
                  label="Manager"
                  error={
                    hasError("manager_id") ? formState.errors.manager_id[0] : ""
                  }
                />
              </Col>

              <Col lg={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="label-input">Tracking</Form.Label>
                  <Select
                    options={trackingOptions}
                    placeholder="Select"
                    className={`multi-select w-100 ${
                      hasError("tracking") ? "is-invalid" : ""
                    }`}
                    classNamePrefix="react-select"
                    isSearchable
                    onChange={handleTrackingChange}
                    value={
                      trackingOptions.find(
                        (o) => o.value === formState.values.tracking
                      ) || null
                    }
                  />
                  {hasError("tracking") && (
                    <div className="invalid-feedback d-block">
                      {formState.errors.tracking[0]}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-2 align-items-center">
              <Col lg={12}>
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="switch"
                    id="program-active-switch"
                    name="is_active"
                    checked={(formState.values.is_active || "y") === "y"}
                    onChange={handleActiveToggle}
                  />
                  <Form.Label
                    htmlFor="program-active-switch"
                    className="label-input mb-0"
                  >
                    {(formState.values.is_active || "y") === "y"
                      ? "Active"
                      : "Inactive"}
                  </Form.Label>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="label-input">Employer ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="employer_id"
                    placeholder="Enter employer ID"
                    className="input-field"
                    onChange={handleChange}
                    value={formState.values.employer_id || ""}
                  />
                  {hasError("employer_id") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.employer_id[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="label-input">Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="postal_code"
                    placeholder="Enter postal code"
                    className="input-field"
                    onChange={handleChange}
                    value={formState.values.postal_code || ""}
                  />
                  {hasError("postal_code") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.postal_code[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="label-input">State</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    placeholder="Enter state"
                    className="input-field"
                    onChange={handleChange}
                    value={formState.values.state || ""}
                  />
                  {hasError("state") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.state[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="label-input">Payment Term</Form.Label>
                  <Form.Control
                    type="text"
                    name="payment_term"
                    placeholder="Enter payment term"
                    className="input-field"
                    onChange={handleChange}
                    value={formState.values.payment_term || ""}
                  />
                  {hasError("payment_term") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.payment_term[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col lg={12}>
                <Form.Group className="mb-2">
                  <Form.Label className="label-input">Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    placeholder="Enter address"
                    className="input-field"
                    onChange={handleChange}
                    value={formState.values.address || ""}
                  />
                  {hasError("address") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.address[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col lg={12}>
                <Form.Group className="mb-2">
                  <Form.Label className="label-input">Emails (Comma Separated)</Form.Label>
                  <Form.Control
                    type="text"
                    name="emails"
                    placeholder="e.g. john@example.com, jane@example.com"
                    className="input-field"
                    onChange={handleChange}
                    value={formState.values.emails || ""}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col lg={6}>
                <button
                  type="button"
                  className="c-b-btn w-100"
                  onClick={onModelClose}
                >
                  Cancel
                </button>
              </Col>
              <Col lg={6}>
                <button
                  type="submit"
                  className="n-u-btn w-100"
                  disabled={!formState.isValid}
                >
                  {isEdit ? "Update" : "Add"}
                </button>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default AddUpdateModal;
